import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { updateAsanaName } from 'services/asana/updateAsanaName';

function CreatePosePage() {
  const [asanaName, setAsanaName] = useState('');
  const [posturenumber, setPosturenumber] = useState('');
  const location = useLocation();
  const { posture, courseId, source } = location.state || {}; // Include source

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { mutate, isLoading, isError, error } = useMutation({
    mutationFn: updateAsanaName,
    onSuccess: async (data) => {
      if (data.status) {
        toast.success('Asana Updated Successfully');
        await queryClient.invalidateQueries(['asanas', courseId]);
        navigate('/pose-instructor', {
          state: {
            posture: {
              postureId: posture.postureId,
              posturename: asanaName,
              posturenumber: parseInt(posturenumber, 10),
              landmarks: posture.landmarks || [],
              courseId: Number(courseId)
            },
            source: source || 'instructor' // Pass source, default to 'instructor'
          }
        });
      } else {
        throw new Error(data.message || 'Failed to update asana');
      }
    },
    onError: (error) => {
      console.error('Asana update error:', error);
      toast.error('Failed to update asana: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    }
  });

  const handleUpdatePose = () => {
    if (!asanaName || !posturenumber || !courseId || !posture.postureId) {
      toast.error('Invalid input: Asana name, posture number, course ID, and posture ID are required.');
      return;
    }

    console.log('Updating asana with payload:', {
      postureid: posture.postureId,
      posturename: asanaName
    });

    mutate({
      postureid: posture.postureId,
      posturename: asanaName
    });
  };

  useEffect(() => {
    if (!posture || !courseId || !posture.postureId) {
      toast.error('Missing posture or course data');
      navigate('/instructor');
    } else {
      setAsanaName(posture.posturename || '');
      setPosturenumber(posture.posturenumber?.toString() || '');
      console.log('CreatePosePage state:', { posture, courseId, source });
    }
  }, [posture, courseId, source, navigate]);

  return (
    <Box sx={{ padding: 3, display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ width: '100%', maxWidth: 400 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
          Update Asana
        </Typography>

        <TextField
          label="Asana Name"
          variant="outlined"
          value={asanaName}
          onChange={(e) => setAsanaName(e.target.value)}
          sx={{ mb: 2, width: '100%' }}
          required
        />
        <TextField label="Posture Number" variant="outlined" type="number" value={posturenumber} disabled sx={{ mb: 2, width: '100%' }} />
        {isError && (
          <Typography color="error" sx={{ mb: 2 }}>
            Error: {error.response?.data?.message || error.message || 'Unknown error'}
          </Typography>
        )}
        <Button variant="contained" color="primary" onClick={handleUpdatePose} disabled={isLoading} sx={{ mt: 2, width: '100%' }}>
          {isLoading ? 'Updating...' : 'Update Asana'}
        </Button>
      </Box>
    </Box>
  );
}

export default CreatePosePage;
